import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { confirmPasswordReset } from "../api/auth";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const uid = searchParams.get("uid") ?? "";
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Show the bad-link state right away — saves the user the click + the
  // backend round-trip when the URL is obviously malformed.
  const linkLooksBroken = !uid || !token;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await confirmPasswordReset(uid, token, password);
      setDone(true);
      // Send them to /login after a brief read of the success message.
      window.setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response
        ?.data?.detail;
      setError(detail ?? "Couldn't reset your password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Set a new password</h1>

        {linkLooksBroken ? (
          <>
            <p className="mt-3 text-slate-600">
              This reset link is incomplete. Open the link from your email exactly as
              it was sent, or request a new one.
            </p>
            <p className="mt-6 text-sm text-slate-600">
              <Link to="/forgot-password" className="font-medium text-slate-900 underline">
                Request a new reset link
              </Link>
            </p>
          </>
        ) : done ? (
          <>
            <p className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
              Password updated. Redirecting to sign in…
            </p>
          </>
        ) : (
          <>
            <p className="mt-2 text-slate-600">
              Pick a new password for your KraetdNet account.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="password">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 p-3"
                  minLength={12}
                  required
                  autoComplete="new-password"
                />
                <p className="mt-1 text-xs text-slate-500">
                  At least 12 characters. Don't reuse a password from another site.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700" htmlFor="confirm">
                  Confirm new password
                </label>
                <input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 p-3"
                  minLength={12}
                  required
                  autoComplete="new-password"
                />
              </div>

              {error ? (
                <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting || !password || !confirm}
                className="no-tap-highlight w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
              >
                {submitting ? "Saving…" : "Save new password"}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
